import React from 'react'
import { Link } from '@inertiajs/react'
import { ArrowLeft, Building2, Briefcase } from 'lucide-react'
import OnboardingStepper from '@/components/onboarding/OnboardingStepper'
import { ToastProvider } from '@/components/ToastProvider'
import FlashHandler from '@/components/FlashHandler'
import { Wordmark } from '@/components/Wordmark'
import { ONBOARDING_STEPS } from '@/lib/onboarding'

interface ContextChip {
  /** Quiet prefix, e.g. "Adding to" (a business) or "Setting up" (a property). */
  label: string
  value: string
  kind: 'account' | 'property'
}

interface OnboardingShellProps {
  /** 1-based station on the Connect → Choose property → Review → Finish rail. */
  currentStep: number
  /** Lets the stepper walk BACK to completed stations (Index passes this for step 1↔2). */
  onStepSelect?: (stepNumber: number) => void
  /** Who/what this setup is for — account before a property exists, the property after. */
  contextChip?: ContextChip
  signedInAs: string
  /**
   * The business's portfolio briefing (`currentAccount.basePath` / `targetAccount.basePath`),
   * served by the backend — the wordmark's destination and the default way out. Onboarding runs
   * without an :account_slug in the path, so the shell has to be TOLD which business it is in;
   * it never assembles one. Null only before an account resolves, and then the wordmark is inert.
   */
  portfolioPath: string | null
  /** Where "Exit setup" leads, when it is somewhere other than the briefing. */
  exitHref?: string
  /** Review needs a broader canvas than the connect forms. */
  wide?: boolean
  children: React.ReactNode
}

/**
 * The one visual world for the whole property-onboarding journey. Every step —
 * whether it has property context (Review, Finish) or not (Connect, Choose) —
 * renders inside this shell, so the operator never falls out of the wizard into
 * marketing chrome or the full app sidebar mid-journey. Operator design system
 * only: DM Sans, teal, cards on the slate background.
 */
export default function OnboardingShell({
  currentStep,
  onStepSelect,
  contextChip,
  signedInAs,
  portfolioPath,
  exitHref,
  wide = false,
  children,
}: OnboardingShellProps) {
  const widthClass = wide ? 'max-w-4xl' : 'max-w-3xl'
  const ChipIcon = contextChip?.kind === 'property' ? Building2 : Briefcase
  // A caller-supplied exit wins; otherwise the way out is the briefing. Both are backend-served.
  const exitTo = exitHref ?? portfolioPath

  return (
    <ToastProvider>
      <FlashHandler />
      <div className="relative flex min-h-screen flex-col bg-background">
        {/* Atmosphere — a soft teal wash pooling from the top, in the operator palette. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-80"
          style={{
            background:
              'radial-gradient(60% 100% at 50% 0%, rgba(13, 148, 136, 0.07) 0%, rgba(13, 148, 136, 0.03) 45%, transparent 100%)',
          }}
        />

        {/* Header — brand lockup, setup context, and a way out. */}
        <header className="relative border-b border-border/60 bg-white/70 backdrop-blur-md">
          <div className={`mx-auto flex ${widthClass} items-center gap-3 px-4 py-3.5 sm:px-6`}>
            {portfolioPath ? (
              <Link
                href={portfolioPath}
                className="flex items-baseline gap-2.5 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                aria-label="Back to your portfolio"
              >
                <Wordmark className="text-lg font-bold tracking-tight text-foreground" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Property setup
                </span>
              </Link>
            ) : (
              <span className="flex items-baseline gap-2.5">
                <Wordmark className="text-lg font-bold tracking-tight text-foreground" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Property setup
                </span>
              </span>
            )}

            <div className="ml-auto flex items-center gap-3">
              {contextChip && (
                <span className="hidden items-center gap-1.5 rounded-lg border border-border/60 bg-white px-2.5 py-1 text-xs font-medium text-muted-foreground shadow-sm sm:flex">
                  <ChipIcon className="h-3 w-3 text-primary" />
                  {contextChip.label}
                  <span className="font-semibold text-foreground">{contextChip.value}</span>
                </span>
              )}
              {exitTo && (
                <Link
                  href={exitTo}
                  className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Exit setup
                </Link>
              )}
            </div>
          </div>
        </header>

        {/* Mobile context chip — the header row is too tight for it on small screens. */}
        {contextChip && (
          <div className="relative border-b border-border/40 bg-white/50 px-4 py-2 sm:hidden">
            <span className="flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground">
              <ChipIcon className="h-3 w-3 text-primary" />
              {contextChip.label}
              <span className="font-semibold text-foreground">{contextChip.value}</span>
            </span>
          </div>
        )}

        {/* The persistent journey rail. */}
        <div className={`relative mx-auto w-full ${widthClass} px-4 pb-2 pt-7 sm:px-6`}>
          <div className="animate-fade-in-up">
            <OnboardingStepper
              currentStep={currentStep}
              steps={ONBOARDING_STEPS}
              onStepSelect={onStepSelect}
            />
          </div>
        </div>

        <main className={`relative mx-auto w-full flex-1 ${widthClass} px-4 pb-16 pt-6 sm:px-6`}>
          {children}
        </main>

        <footer className="relative border-t border-border/60 bg-white/70 py-3 backdrop-blur-md">
          <p className="text-center text-xs text-muted-foreground">
            Signed in as <span className="font-medium text-foreground/80">{signedInAs}</span>
          </p>
        </footer>
      </div>
    </ToastProvider>
  )
}
